import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import API from '../api';

const DAY_LABELS = ['Ponedeljak', 'Utorak', 'Sreda', 'Četvrtak', 'Petak', 'Subota', 'Nedelja'];
// Blaga, različita nijansa po danu u nedelji - samo radi lakšeg vizuelnog razlikovanja
// redova u listi, ne nosi nikakvo drugo značenje.
const DAY_ROW_COLORS = ['#eff6ff', '#f0fdf4', '#fefce8', '#fff7ed', '#fdf2f8', '#f5f3ff', '#f1f5f9'];
const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = ['00', '15', '30', '45'];

const pad = (n) => String(n).padStart(2, '0');
const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const formatShort = (dateStr) => {
    const [y, m, d] = dateStr.split('-');
    return `${d}.${m}.${y}.`;
};

// Ponedeljak nedelje kojoj pripada dati datum (getDay(): 0=nedelja...6=subota).
const mondayOf = (date) => {
    const d = new Date(date);
    const dow = d.getDay();
    const diff = dow === 0 ? -6 : 1 - dow;
    d.setDate(d.getDate() + diff);
    return d;
};

// ISO 8601 broj nedelje za dati datum.
const isoWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

// Ponedeljak ISO nedelje `week` u godini `year`.
const mondayOfIsoWeek = (year, week) => {
    const jan4 = new Date(year, 0, 4);
    const jan4Day = jan4.getDay() || 7;
    const week1Monday = new Date(jan4);
    week1Monday.setDate(jan4.getDate() - jan4Day + 1);
    const target = new Date(week1Monday);
    target.setDate(week1Monday.getDate() + (week - 1) * 7);
    return target;
};

// Da li godina ima 52 ili 53 ISO nedelje.
const isoWeeksInYear = (year) => {
    const p = (y) => (y + Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400)) % 7;
    return (p(year) === 4 || p(year - 1) === 3) ? 53 : 52;
};

// Podrazumevano radno vreme po danu u nedelji - važi dok se eksplicitno ne promeni ili
// zatvori za konkretan datum (mora se poklapati sa AppointmentService.defaultHoursFor na backendu).
const defaultHoursForDayIndex = (i) => {
    if (i === 5) return { startTime: '09:00', endTime: '20:00' }; // subota
    if (i === 1) return { startTime: '11:00', endTime: '21:00' }; // utorak
    return { startTime: '14:00', endTime: '21:00' }; // pon, sre, čet, pet, ned
};

const buildWeek = (mondayDate) => {
    return DAY_LABELS.map((label, i) => {
        const d = new Date(mondayDate);
        d.setDate(d.getDate() + i);
        return { date: toIso(d), label, ...defaultHoursForDayIndex(i), closed: false, doctorIds: [], isDefault: true };
    });
};

const today = new Date();
const todayIsoWeek = isoWeekNumber(today);
const todayYear = today.getFullYear();

const WorkingHoursPanel = () => {
    const [displayedMonday, setDisplayedMonday] = useState(mondayOf(today));
    const [tableYear, setTableYear] = useState(todayYear);
    const [week, setWeek] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [saving, setSaving] = useState(false);
    const [saveResult, setSaveResult] = useState(null);

    useEffect(() => {
        API.get('/doctors').then(res => setDoctors(res.data)).catch(() => setDoctors([]));
    }, []);

    const displayedWeekNumber = isoWeekNumber(displayedMonday);
    const isCurrentWeek = displayedWeekNumber === todayIsoWeek && displayedMonday.getFullYear() === todayYear;

    useEffect(() => {
        const emptyWeek = buildWeek(displayedMonday);
        const startDate = emptyWeek[0].date;
        const endDate = emptyWeek[6].date;

        API.get('/working-hours', { params: { startDate, endDate } })
            .then(res => {
                const byDate = {};
                res.data.forEach(w => { byDate[w.date] = w; });
                setWeek(emptyWeek.map(day => {
                    const existing = byDate[day.date];
                    if (!existing) return day;
                    return {
                        ...day,
                        closed: existing.closed,
                        startTime: existing.startTime ? existing.startTime.substring(0, 5) : '',
                        endTime: existing.endTime ? existing.endTime.substring(0, 5) : '',
                        doctorIds: existing.doctorIds || [],
                        isDefault: false
                    };
                }));
            })
            .catch(() => setWeek(emptyWeek));

        setSaveResult(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayedMonday]);

    const goToWeek = (offsetWeeks) => {
        const d = new Date(displayedMonday);
        d.setDate(d.getDate() + offsetWeeks * 7);
        setDisplayedMonday(d);
    };

    const goToWeekNumber = (weekNum) => {
        setDisplayedMonday(mondayOfIsoWeek(tableYear, weekNum));
    };

    const updateDay = (index, field, value) => {
        setWeek(prev => prev.map((day, i) => i === index ? { ...day, [field]: value, isDefault: false } : day));
    };

    // Prazna lista doctorIds = nije podešeno ko radi taj dan, pa se pri zakazivanju nude svi doktori.
    const toggleDoctor = (index, doctorId) => {
        setWeek(prev => prev.map((day, i) => {
            if (i !== index) return day;
            const has = day.doctorIds.includes(doctorId);
            const doctorIds = has ? day.doctorIds.filter(id => id !== doctorId) : [...day.doctorIds, doctorId];
            return { ...day, doctorIds, isDefault: false };
        }));
    };

    // "Zatvoreno" je EKSPLICITNO zatvaranje tog dana (npr. praznik), različito od "koristi
    // podrazumevano" - ne sme da se pomeša sa podrazumevanim obrascem koji bi taj dan inače
    // ostavio otvorenim.
    const toggleClosed = (index) => {
        setWeek(prev => prev.map((day, i) => {
            if (i !== index) return day;
            const closed = !day.closed;
            return closed
                ? { ...day, closed: true, startTime: '', endTime: '', isDefault: false }
                : { ...day, closed: false, ...defaultHoursForDayIndex(DAY_LABELS.indexOf(day.label)), isDefault: false };
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        setSaveResult(null);
        try {
            await API.put('/working-hours/days', {
                days: week.map(day => ({
                    date: day.date,
                    startTime: day.closed ? null : (day.startTime || null),
                    endTime: day.closed ? null : (day.endTime || null),
                    closed: day.closed,
                    doctorIds: day.doctorIds
                }))
            });
            setSaveResult('Radno vreme sačuvano.');
            setWeek(prev => prev.map(day => ({ ...day, isDefault: false })));
        } catch (err) {
            setSaveResult(err.response?.data?.message || 'Greška pri čuvanju.');
        } finally {
            setSaving(false);
        }
    };

    // Vojno (24h) vreme birano preko dva dropdown-a, umesto <input type="time"> čiji
    // prikaz (AM/PM ili 24h) zavisi od OS/browser podešavanja i ne može pouzdano da se
    // forsira na 24h iz koda.
    const renderTimeDropdown = (value, onChange) => {
        const [h, m] = value ? value.split(':') : ['', ''];
        return (
            <span style={styles.timeGroup}>
                <select
                    style={styles.timeSelect}
                    value={h}
                    onChange={(e) => onChange(e.target.value ? `${e.target.value}:${m || '00'}` : '')}
                >
                    <option value="">--</option>
                    {HOURS.map(hh => <option key={hh} value={hh}>{hh}</option>)}
                </select>
                <span>:</span>
                <select
                    style={styles.timeSelect}
                    value={m}
                    onChange={(e) => onChange(`${h || '00'}:${e.target.value}`)}
                    disabled={!h}
                >
                    <option value="">--</option>
                    {MINUTES.map(mm => <option key={mm} value={mm}>{mm}</option>)}
                </select>
            </span>
        );
    };

    if (week.length === 0) return <div style={styles.loader}>Učitavanje...</div>;

    const totalWeeks = isoWeeksInYear(tableYear);
    const weekNumbers = Array.from({ length: totalWeeks }, (_, i) => i + 1);

    return (
        <div style={styles.container}>
            <h2 style={styles.title}>Radno vreme ordinacije</h2>
            <p style={styles.hint}>
                Radno vreme važi za CELU ordinaciju, ne pojedinačnog doktora. Podrazumevano radno vreme (subota
                09-20h, utorak 11-21h, ostali dani 14-21h) se primenjuje automatski dok se konkretan dan ne izmeni
                ili izričito zatvori (npr. za praznik) - nije potrebno ručno potvrđivati svaku nedelju.
                Označite i koji doktori tog dana rade — ako za neki dan nijedan nije označen, pri zakazivanju
                (grupno zakazivanje, sveska) mogu se birati svi doktori.
            </p>

            {/* Tabela brojeva nedelja u godini - klik odmah vodi na tu nedelju */}
            <div style={styles.yearBar}>
                <button style={styles.navBtn} onClick={() => setTableYear(tableYear - 1)} aria-label="Prethodna godina">
                    <ChevronLeft size={18} />
                </button>
                <span style={styles.yearLabel}>{tableYear}.</span>
                <button style={styles.navBtn} onClick={() => setTableYear(tableYear + 1)} aria-label="Sledeća godina">
                    <ChevronRight size={18} />
                </button>
            </div>
            <div style={styles.weekGrid}>
                {weekNumbers.map(w => {
                    const isSelected = w === displayedWeekNumber && tableYear === displayedMonday.getFullYear();
                    const isToday = w === todayIsoWeek && tableYear === todayYear;
                    let cellStyle = styles.weekCell;
                    if (isSelected) cellStyle = { ...cellStyle, ...styles.weekCellSelected };
                    else if (isToday) cellStyle = { ...cellStyle, ...styles.weekCellToday };
                    return (
                        <button key={w} style={cellStyle} onClick={() => goToWeekNumber(w)}>
                            {w}
                        </button>
                    );
                })}
            </div>

            <div style={{ ...styles.weekNav, ...(isCurrentWeek ? styles.weekNavCurrent : {}) }}>
                <button style={styles.navBtn} onClick={() => goToWeek(-1)} aria-label="Prethodna nedelja">
                    <ChevronLeft size={20} />
                </button>
                <div style={styles.weekNavCenter}>
                    <div style={styles.weekRange}>
                        Nedelja {displayedWeekNumber} — {formatShort(week[0].date)} – {formatShort(week[6].date)}
                    </div>
                    {isCurrentWeek && <span style={styles.currentBadge}>Tekuća nedelja</span>}
                </div>
                <button style={styles.navBtn} onClick={() => goToWeek(1)} aria-label="Sledeća nedelja">
                    <ChevronRight size={20} />
                </button>
            </div>

            <div style={styles.weekList}>
                {week.map((day, index) => (
                    <div key={day.date} style={{ ...styles.dayRow, backgroundColor: DAY_ROW_COLORS[index] }}>
                        <div style={styles.dayLabelBox}>
                            <div style={styles.dayName}>{day.label}</div>
                            <div style={styles.dayDate}>{formatShort(day.date)}</div>
                            {day.isDefault && !day.closed && <div style={styles.defaultTag}>podrazumevano</div>}
                        </div>
                        {day.closed ? (
                            <span style={styles.closedLabel}>Zatvoreno</span>
                        ) : (
                            <>
                                {renderTimeDropdown(day.startTime, (v) => updateDay(index, 'startTime', v))}
                                <span style={styles.dash}>–</span>
                                {renderTimeDropdown(day.endTime, (v) => updateDay(index, 'endTime', v))}
                            </>
                        )}
                        <button
                            style={day.closed ? styles.reopenBtn : styles.closeBtn}
                            onClick={() => toggleClosed(index)}
                        >
                            {day.closed ? 'Otvori' : 'Zatvori'}
                        </button>

                        {!day.closed && doctors.length > 0 && (
                            <div style={styles.doctorRow}>
                                <span style={styles.doctorRowLabel}>Ko radi:</span>
                                {doctors.map(doc => {
                                    const checked = day.doctorIds.includes(doc.id);
                                    return (
                                        <label key={doc.id} style={{ ...styles.doctorChip, ...(checked ? styles.doctorChipActive : {}) }}>
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggleDoctor(index, doc.id)}
                                                style={styles.doctorCheckbox}
                                            />
                                            {doc.firstName} {doc.lastName}
                                        </label>
                                    );
                                })}
                                {day.doctorIds.length === 0 && (
                                    <span style={styles.doctorHint}>nije podešeno — dozvoljeni svi doktori</span>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <div style={styles.saveRow}>
                <button style={{ ...styles.saveBtn, opacity: saving ? 0.6 : 1 }} onClick={handleSave} disabled={saving}>
                    {saving ? 'Čuvanje...' : 'Sačuvaj radno vreme za ovu nedelju'}
                </button>
                {saveResult && <span style={styles.saveResult}>{saveResult}</span>}
            </div>
        </div>
    );
};

const styles = {
    container: {
        maxWidth: '700px',
        margin: '0 auto',
        padding: '15px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    title: { margin: '0 0 10px 0', fontSize: '24px', color: '#1f2937' },
    hint: { fontSize: '13px', color: '#7f8c8d', marginBottom: '20px', lineHeight: '1.5' },
    loader: { textAlign: 'center', marginTop: '50px' },

    yearBar: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '8px' },
    yearLabel: { fontSize: '15px', fontWeight: '700', color: '#1e293b', minWidth: '50px', textAlign: 'center' },
    weekGrid: {
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(38px, 1fr))', gap: '6px',
        backgroundColor: 'white', padding: '12px', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        marginBottom: '15px'
    },
    weekCell: {
        padding: '8px 0', borderRadius: '6px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc',
        color: '#334155', fontSize: '12px', fontWeight: '600', cursor: 'pointer'
    },
    weekCellToday: { border: '1px solid #1c8aa6', color: '#1c8aa6' },
    weekCellSelected: { backgroundColor: '#1c8aa6', color: 'white', border: '1px solid #1c8aa6' },

    weekNav: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        backgroundColor: '#f1f5f9', borderRadius: '10px', padding: '10px 15px', marginBottom: '15px'
    },
    weekNavCurrent: { backgroundColor: '#e4f4f7', border: '1px solid #8fd0dc' },
    navBtn: {
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: '36px', height: '36px', borderRadius: '8px', border: '1px solid #cbd5e1',
        backgroundColor: 'white', color: '#334155', cursor: 'pointer'
    },
    weekNavCenter: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' },
    weekRange: { fontSize: '15px', fontWeight: '700', color: '#1e293b' },
    currentBadge: {
        fontSize: '11px', fontWeight: 'bold', color: 'white', backgroundColor: '#1c8aa6',
        padding: '2px 10px', borderRadius: '10px'
    },

    weekList: { backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', overflow: 'hidden', marginBottom: '15px' },
    dayRow: {
        display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 15px',
        borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap'
    },
    dayLabelBox: { minWidth: '110px', flex: '1 1 110px' },
    dayName: { fontSize: '14px', fontWeight: '600', color: '#1e293b' },
    dayDate: { fontSize: '11px', color: '#94a3b8' },
    defaultTag: { fontSize: '10px', color: '#0d9488', fontWeight: '600', marginTop: '2px' },
    timeGroup: { display: 'flex', alignItems: 'center', gap: '4px' },
    timeSelect: { padding: '8px 4px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' },
    dash: { color: '#94a3b8' },
    closedLabel: { flex: 1, color: '#b91c1c', fontSize: '13px', fontWeight: '600', fontStyle: 'italic' },
    closeBtn: { padding: '6px 10px', backgroundColor: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', marginLeft: 'auto' },
    reopenBtn: { padding: '6px 10px', backgroundColor: '#dcfce7', color: '#166534', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', marginLeft: 'auto' },

    doctorRow: {
        flexBasis: '100%', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px',
        marginTop: '8px', paddingTop: '8px', borderTop: '1px dashed #e2e8f0'
    },
    doctorRowLabel: { fontSize: '12px', fontWeight: '600', color: '#64748b', marginRight: '2px' },
    doctorChip: {
        display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 10px', borderRadius: '999px',
        border: '1px solid #cbd5e1', backgroundColor: '#f8fafc', color: '#334155', fontSize: '12px',
        cursor: 'pointer', userSelect: 'none'
    },
    doctorChipActive: { backgroundColor: '#e4f4f7', border: '1px solid #1c8aa6', color: '#0d4756', fontWeight: '600' },
    doctorCheckbox: { margin: 0, cursor: 'pointer' },
    doctorHint: { fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' },
    saveRow: { display: 'flex', alignItems: 'center', gap: '15px' },
    saveBtn: { padding: '12px 18px', backgroundColor: '#1c8aa6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },
    saveResult: { fontSize: '13px', color: '#1f2937', fontWeight: '600' }
};

export default WorkingHoursPanel;
